/**
 * Das Kunden-Objektblatt (Einrichten-Neubau P5).
 *
 * Stammdaten mit einem Speichern; die ERP-Kennungen stehen in einer eigenen,
 * erklärten Zone (bei Bestandskunden führend im ERP, hier nur lesbar — genau
 * einmal erklärt statt auf jeder Seite als Hinweis-Kachel). Deaktivieren ist
 * der umkehrbare Zustandsschalter im Kopf — die alte Sackgasse ohne
 * Reaktivieren ist damit Geschichte.
 */

import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../App';
import { LeitstandRahmen } from '../../components/leitstand/LeitstandRahmen';
import { Karte, Notiz, Zustand } from '../../components/leitstand';

const EINGABE =
  'w-full rounded-[4px] border border-linie-2 bg-flaeche-2 px-3 py-2 text-sm text-tinte focus:border-akzent focus:outline-none disabled:opacity-60';
const KNOPF = 'rounded-[4px] px-3 py-1.5 font-zahl text-xs disabled:opacity-50';
const KNOPF_PRIMAER = `${KNOPF} border border-akzent bg-akzent text-akzent-satz`;
const KNOPF_LEISE = `${KNOPF} border border-linie-2 bg-flaeche text-tinte-2`;

interface Kunde {
  id: string;
  name: string;
  shortName?: string | null;
  externalCustomerNo?: string | null;
  externalDeliveryId?: string | null;
  customerGroupId?: string | null;
  isActive: boolean;
}

interface Gruppe {
  id: string;
  name: string;
}

export default function KundeBlatt() {
  const { user } = useAuth();
  const { id = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const istNeu = id === 'neu';

  const [form, setForm] = useState({
    name: '',
    shortName: '',
    externalCustomerNo: '',
    customerGroupId: '',
  });
  const [erp, setErp] = useState<{ nr: string | null; liefer: string | null }>({
    nr: null,
    liefer: null,
  });
  const [aktiv, setAktiv] = useState(true);
  const [gruppen, setGruppen] = useState<Gruppe[]>([]);
  const [laden, setLaden] = useState(!istNeu);
  const [fehler, setFehler] = useState('');
  const [aktionsFehler, setAktionsFehler] = useState('');
  const [gespeichert, setGespeichert] = useState('');
  const [speichert, setSpeichert] = useState(false);
  const [schalterFrage, setSchalterFrage] = useState(false);

  const [verwendung, setVerwendung] = useState<
    Array<{ text: string; wert: number; ziel?: string }>
  >([]);
  const [verlauf, setVerlauf] = useState<Array<{ wann: string; wer: string; was: string }>>([]);

  const lade = useCallback(async () => {
    const gRes = await fetch('/api/einrichten/objekt/kundengruppen', { credentials: 'include' });
    if (gRes.ok) setGruppen(((await gRes.json()).objekte ?? []) as Gruppe[]);
    if (istNeu) return;
    setLaden(true);
    setFehler('');
    try {
      const [oRes, vRes, hRes] = await Promise.all([
        fetch(`/api/einrichten/objekt/kunden/${id}`, { credentials: 'include' }),
        fetch(`/api/einrichten/objekt/kunden/${id}/verwendung`, { credentials: 'include' }),
        fetch(`/api/einrichten/objekt/kunden/${id}/verlauf`, { credentials: 'include' }),
      ]);
      if (!oRes.ok) throw new Error('Der Kunde konnte nicht geladen werden');
      const o = (await oRes.json()).objekt as Kunde;
      setForm({
        name: o.name ?? '',
        shortName: o.shortName ?? '',
        externalCustomerNo: o.externalCustomerNo ?? '',
        customerGroupId: o.customerGroupId ?? '',
      });
      setErp({ nr: o.externalCustomerNo ?? null, liefer: o.externalDeliveryId ?? null });
      setAktiv(!!o.isActive);
      if (vRes.ok) setVerwendung((await vRes.json()).zeilen ?? []);
      if (hRes.ok) setVerlauf((await hRes.json()).zeilen ?? []);
    } catch (e) {
      setFehler(e instanceof Error ? e.message : 'Der Kunde konnte nicht geladen werden');
    } finally {
      setLaden(false);
    }
  }, [id, istNeu]);

  useEffect(() => {
    void lade();
  }, [lade]);

  const speichere = async () => {
    if (!form.name.trim()) {
      setAktionsFehler('Bitte einen Namen angeben.');
      return;
    }
    setSpeichert(true);
    setAktionsFehler('');
    try {
      const body: Record<string, unknown> = {
        name: form.name.trim(),
        shortName: form.shortName.trim() || null,
        customerGroupId: form.customerGroupId || null,
      };
      if (istNeu && form.externalCustomerNo.trim())
        body.externalCustomerNo = form.externalCustomerNo.trim();
      const res = await fetch(istNeu ? '/api/admin/customers' : `/api/admin/customers/${id}`, {
        method: istNeu ? 'POST' : 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body),
      });
      const antwort = await res.json().catch(() => null);
      if (!res.ok) throw new Error(antwort?.error || 'Speichern fehlgeschlagen');
      if (istNeu) {
        navigate(antwort?.id ? `/einrichten/kunden/${antwort.id}` : '/einrichten/kunden', {
          replace: true,
        });
        return;
      }
      setGespeichert('✓ Gespeichert');
      setTimeout(() => setGespeichert(''), 2600);
      void lade();
    } catch (e) {
      setAktionsFehler(e instanceof Error ? e.message : 'Speichern fehlgeschlagen');
    } finally {
      setSpeichert(false);
    }
  };

  const schalteZustand = async (ziel: boolean) => {
    try {
      const res = await fetch(`/api/einrichten/objekt/kunden/${id}/zustand`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ aktiv: ziel }),
      });
      if (!res.ok)
        throw new Error(
          (await res.json().catch(() => null))?.error || 'Zustand konnte nicht geändert werden',
        );
      setAktiv(ziel);
      setSchalterFrage(false);
      void lade();
    } catch (e) {
      setAktionsFehler(e instanceof Error ? e.message : 'Zustand konnte nicht geändert werden');
    }
  };

  return (
    <LeitstandRahmen
      titel={istNeu ? 'Kunde anlegen' : form.name || 'Kunde'}
      benutzer={user?.displayName}
      werkzeuge={
        !istNeu && !laden ? (
          <span className="flex items-center gap-3">
            {aktiv ? (
              <Zustand art="gut">aktiv</Zustand>
            ) : (
              <span className="font-zahl text-[11px] text-tinte-3">außer Betrieb</span>
            )}
            <button
              onClick={() => (aktiv ? setSchalterFrage(true) : void schalteZustand(true))}
              className={KNOPF_LEISE}
            >
              {aktiv ? 'Außer Betrieb nehmen' : 'Wieder in Betrieb nehmen'}
            </button>
          </span>
        ) : undefined
      }
    >
      <div className="flex flex-col gap-6">
        <Link to="/einrichten/kunden" className="text-[13px] text-tinte-3 hover:text-tinte-2">
          ← Kunden
        </Link>

        {schalterFrage && (
          <div className="flex flex-wrap items-center gap-3 rounded-leitstand border border-linie-2 bg-flaeche px-4 py-3">
            <span className="text-[13.5px] text-tinte-2">
              {form.name} verschwindet dann aus der Auswahl an den Stationen. Erfasste Waschgänge
              und Einträge bleiben unverändert — Wiederinbetriebnahme jederzeit hier.
            </span>
            <button onClick={() => void schalteZustand(false)} className={KNOPF_PRIMAER}>
              Außer Betrieb nehmen
            </button>
            <button onClick={() => setSchalterFrage(false)} className={KNOPF_LEISE}>
              Abbrechen
            </button>
          </div>
        )}

        {fehler && (
          <Notiz titel="Konnte nicht laden">
            {fehler}{' '}
            <button onClick={() => void lade()} className="underline">
              Erneut versuchen
            </button>
          </Notiz>
        )}

        {laden && !fehler && <p className="py-12 text-center text-sm text-tinte-3">Lade Kunde …</p>}

        {!laden && !fehler && (
          <>
            <Karte titel="Stammdaten" marke="ein Speichern, eine Rückmeldung">
              <div className="flex flex-col gap-4">
                <div className="grid gap-4 [grid-template-columns:repeat(auto-fit,minmax(220px,1fr))]">
                  <label className="flex flex-col gap-1.5">
                    <span className="font-zahl text-[10px] uppercase tracking-[0.13em] text-tinte-3">
                      Name *
                    </span>
                    <input
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      className={EINGABE}
                    />
                  </label>
                  <label className="flex flex-col gap-1.5">
                    <span className="font-zahl text-[10px] uppercase tracking-[0.13em] text-tinte-3">
                      Kürzel
                    </span>
                    <input
                      value={form.shortName}
                      onChange={(e) => setForm({ ...form, shortName: e.target.value })}
                      placeholder="fürs schnelle Finden an der Station"
                      className={EINGABE}
                    />
                  </label>
                  <label className="flex flex-col gap-1.5">
                    <span className="font-zahl text-[10px] uppercase tracking-[0.13em] text-tinte-3">
                      Kundengruppe
                    </span>
                    <select
                      value={form.customerGroupId}
                      onChange={(e) => setForm({ ...form, customerGroupId: e.target.value })}
                      className={EINGABE}
                    >
                      <option value="">— keine Gruppe —</option>
                      {gruppen.map((g) => (
                        <option key={g.id} value={g.id}>
                          {g.name}
                        </option>
                      ))}
                    </select>
                    {form.customerGroupId && (
                      <Link
                        to={`/einrichten/kundengruppen/${form.customerGroupId}`}
                        className="text-xs text-akzent hover:underline"
                      >
                        Gruppe öffnen →
                      </Link>
                    )}
                  </label>
                  {istNeu && (
                    <label className="flex flex-col gap-1.5">
                      <span className="font-zahl text-[10px] uppercase tracking-[0.13em] text-tinte-3">
                        Kundennr. (optional)
                      </span>
                      <input
                        value={form.externalCustomerNo}
                        onChange={(e) => setForm({ ...form, externalCustomerNo: e.target.value })}
                        className={EINGABE}
                      />
                      <span className="text-xs text-tinte-3">
                        Sobald der ERP-Import den Kunden kennt, führt das ERP.
                      </span>
                    </label>
                  )}
                </div>
                {aktionsFehler && <p className="text-[13px] text-stoer">{aktionsFehler}</p>}
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => void speichere()}
                    disabled={speichert}
                    className={KNOPF_PRIMAER}
                  >
                    {istNeu ? 'Anlegen' : 'Speichern'}
                  </button>
                  {gespeichert && (
                    <span className="font-zahl text-[13px] text-lauf">{gespeichert}</span>
                  )}
                </div>
                {!istNeu && (erp.nr || erp.liefer) && (
                  <details className="text-[12.5px] text-tinte-3">
                    <summary className="cursor-pointer font-zahl text-[11px] uppercase tracking-[0.13em]">
                      ERP-Kennungen
                    </summary>
                    <p className="mt-1.5">
                      Kundennr. <span className="font-zahl text-tinte-2">{erp.nr ?? '—'}</span> ·
                      Lieferadr.-Nr.{' '}
                      <span className="font-zahl text-tinte-2">{erp.liefer ?? '—'}</span> — geführt
                      im ERP, aktualisiert durch den Import; hier nicht änderbar.
                    </p>
                  </details>
                )}
              </div>
            </Karte>

            {!istNeu && (
              <Karte titel="Verwendung" marke="Wirkung sichtbar, bevor man handelt">
                {verwendung.length === 0 ? (
                  <p className="py-4 text-center text-sm text-tinte-3">Noch nichts erfasst.</p>
                ) : (
                  <div className="flex flex-col">
                    {verwendung.map((v, i) => {
                      const inhalt = (
                        <>
                          <span className="font-zahl text-[15px] font-medium">
                            {v.wert.toLocaleString('de-DE')}
                          </span>
                          <span className="text-sm">{v.text}</span>
                          {v.ziel && <span className="ml-auto font-zahl text-tinte-3">→</span>}
                        </>
                      );
                      const klasse = `flex items-center gap-3 px-1 py-3 ${i < verwendung.length - 1 ? 'border-b border-linie' : ''}`;
                      return v.ziel ? (
                        <Link key={v.text} to={v.ziel} className={`${klasse} hover:bg-flaeche-2`}>
                          {inhalt}
                        </Link>
                      ) : (
                        <div key={v.text} className={klasse}>
                          {inhalt}
                        </div>
                      );
                    })}
                  </div>
                )}
              </Karte>
            )}

            {!istNeu && (
              <Karte titel="Verlauf" marke="aus dem Protokoll, neueste zuerst">
                {verlauf.length === 0 ? (
                  <p className="py-4 text-center text-sm text-tinte-3">
                    Noch keine Einträge im Protokoll.
                  </p>
                ) : (
                  <div className="flex flex-col">
                    {verlauf.map((z, i) => (
                      <div
                        key={`${z.wann}-${i}`}
                        className={`grid grid-cols-[110px_130px_1fr] gap-3 px-1 py-2.5 text-[13px] max-sm:grid-cols-[90px_1fr] ${
                          i < verlauf.length - 1 ? 'border-b border-linie' : ''
                        }`}
                      >
                        <span className="font-zahl text-xs text-tinte-3">
                          {new Date(z.wann).toLocaleString('de-DE', {
                            timeZone: 'Europe/Berlin',
                            day: '2-digit',
                            month: '2-digit',
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </span>
                        <span className="text-tinte-2">{z.wer}</span>
                        <span className="max-sm:col-span-2">{z.was}</span>
                      </div>
                    ))}
                  </div>
                )}
              </Karte>
            )}
          </>
        )}
      </div>
    </LeitstandRahmen>
  );
}
